import { useSettings } from "../../../contexts/SettingsManager/SettingsManager";
import {
  addRollupValues,
  calcMetric,
  getPredictedFutureMetric,
} from "../../../utils/metrics";
import {
  useFilteredRollups,
  useGetPageFromPath,
  useSelectedShift,
  useDashboardPageContext,
  useVisibleProcesses,
  useFullRangeDataDump,
  useSingleProcessFullRangeDataDump,
  useIsSingleProcessPage,
  useWorkScheduleIdOnCurrentPage,
} from "../../../utils/customHooks/genericHooks";
import { useFlightTranslation } from "../../../utils/customHooks/useFlightTranslation";
import { useMapDataDumpToRollup } from "../TrendChart/TrendChartContent/trendChartContentUtils";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useShouldUseDataDumpsForGroupMetrics } from "utils/customHooks/whichServerHooks";
import {
  useFlightConfig,
  useMetricsForWorkSchedule,
} from "components/DataGetter/FlightConfig/useFlightConfig";
import { useMemo } from "react";
import { DataDumpAggregateType } from "types/types";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { useSelector } from "store/useSelector";
import { GroupMetricType } from "components/DataGetter/FlightConfig/FlightConfigTypes/GroupMetricType";
import { useFilterByFlightConfig } from "./useVisibleMetrics";

/** starter pokemons (if flightConfig.metrics is not configured) - these are available from Askr */
export const DEFAULT_ASKR_METRICS: GroupMetricType[] = [
  {
    displayName: "TEEP",
    dataDumpKey: "teepPercent",
    units: "%",
  },
  {
    displayName: "Availability",
    dataDumpKey: "availabilityPercent",
    units: "%",
  },
  {
    displayName: "Uptime",
    dataDumpKey: "uptimePercent",
    units: "%",
  },
];

/** use dataDumpKey = e.g. "current_performance_percent" to access one of these custom metrics */
const CUSTOM_METRICS: {
  [key: string]: Partial<GroupMetricType> & {
    getValue: (dataDump: DataDumpAggregateType | null) => number | null;
  };
} = {
  /** Current Rate / Target Rate — first requested by steris https://app.clickup.com/t/14117868/OPS-42531 */
  current_performance_percent: {
    description: "(*Instantaneous Performance) = Current Rate / Target Rate",
    getValue: (dataDump: DataDumpAggregateType | null) => {
      if (!dataDump?.lastSegment) {
        return null;
      }
      const { targetRate, actualRate } = dataDump.lastSegment;

      const currentPerformancePercent =
        targetRate && actualRate ? (actualRate / targetRate) * 100 : null;
      return currentPerformancePercent;
    },
  },
};

/**
 * Calculates the result of a custom metric expression using values from the data dump
 *
 * Supports complex expressions with multiple operations and numeric constants.
 * Follows standard operator precedence (* and / evaluated before + and -).
 *
 * Example:
 * - "waitingTime/productionTime" will divide waitingTime by productionTime
 * - "waitingTime/productionTime*100" will calculate as a percentage
 * - "productionTime/60000" will convert milliseconds to minutes
 *
 * @param expression The expression to evaluate (e.g. "waitingTime/productionTime*100")
 * @param dataDump The data dump containing values to use in the calculation
 * @returns The calculated value or null if calculation fails or results in NaN/Infinity
 */
export function calculateCustomMetric(
  expression: string,
  dataDump: DataDumpAggregateType | null
): number | null {
  if (!dataDump) {
    return null;
  }

  try {
    // Split the expression based on operators
    const operators = ["/", "*", "+", "-"];

    // Tokenize the expression into operands and operators
    const tokens: string[] = [];
    let currentToken = "";

    for (let i = 0; i < expression.length; i++) {
      const char = expression[i];
      if (operators.includes(char)) {
        if (currentToken) {
          tokens.push(currentToken.trim());
          currentToken = "";
        }
        tokens.push(char);
      } else {
        currentToken += char;
      }
    }

    if (currentToken) {
      tokens.push(currentToken.trim());
    }

    // If there are no operators, just return the value directly
    if (tokens.length === 1) {
      const value =
        typeof dataDump[tokens[0]] === "number"
          ? (dataDump[tokens[0]] as number)
          : null;
      return value;
    }

    // Process the expression from left to right, respecting operator precedence
    // First, resolve all multiplication and division
    const evalExpression = (tokens: string[]): number | null => {
      // Get values for operands from dataDump
      const resolveOperand = (operand: string): number | null => {
        // If operand is a number, return it directly
        if (!isNaN(Number(operand))) {
          return Number(operand);
        }

        return typeof dataDump[operand] === "number"
          ? (dataDump[operand] as number)
          : null;
      };

      // First pass: Handle multiplication and division
      let i = 0;
      const firstPassTokens = [...tokens]; // Copy tokens to avoid modifying the original

      // Process all multiplications and divisions first (left to right)
      while (i < firstPassTokens.length) {
        if (
          i + 2 < firstPassTokens.length &&
          (firstPassTokens[i + 1] === "*" || firstPassTokens[i + 1] === "/")
        ) {
          const leftValue = resolveOperand(firstPassTokens[i]);
          const operator = firstPassTokens[i + 1];
          const rightValue = resolveOperand(firstPassTokens[i + 2]);

          if (leftValue === null || rightValue === null) {
            return null;
          }

          let result: number;
          if (operator === "*") {
            result = leftValue * rightValue;
          } else {
            // operator === "/"
            if (rightValue === 0) {
              return null;
            }
            result = leftValue / rightValue;
          }

          // Replace the operation with its result
          firstPassTokens.splice(i, 3, result.toString());

          // Don't increment i here since we need to check if the next operation is also * or /
        } else {
          i++;
        }
      }

      // Second pass: Handle addition and subtraction
      i = 0;
      const secondPassTokens = [...firstPassTokens]; // Copy for second pass

      // Process all additions and subtractions (left to right)
      while (i < secondPassTokens.length) {
        if (
          i + 2 < secondPassTokens.length &&
          (secondPassTokens[i + 1] === "+" || secondPassTokens[i + 1] === "-")
        ) {
          const leftValue = resolveOperand(secondPassTokens[i]);
          const operator = secondPassTokens[i + 1];
          const rightValue = resolveOperand(secondPassTokens[i + 2]);

          if (leftValue === null || rightValue === null) {
            return null;
          }

          let result: number;
          if (operator === "+") {
            result = leftValue + rightValue;
          } else {
            // operator === "-"
            result = leftValue - rightValue;
          }

          // Replace the operation with its result
          secondPassTokens.splice(i, 3, result.toString());

          // Don't increment i here for the same reason
        } else {
          i++;
        }
      }

      // The final result should be the only item left in the tokens array
      if (secondPassTokens.length !== 1) {
        return null; // Something went wrong
      }

      return resolveOperand(secondPassTokens[0]);
    };

    const result = evalExpression(tokens);

    // Check for valid result
    if (result === null || isNaN(result) || !isFinite(result)) {
      return null;
    }

    return result;
  } catch (error) {
    console.error("Error calculating custom metric:", error);
    return null;
  }
}

export function useMetrics(dataDumpToCheckLoading?: DataDumpAggregateType): {
  /** metrics configured in flightConfig.metrics else DEFAULT_METRICS */
  metrics: GroupMetricType[];
  metricsRaw: GroupMetricType[] | undefined;
  loading: boolean;
  preferredMetricData: GroupMetricType;
  shouldShowPredictedMetric: boolean;
} {
  const flightConfigMetrics = useMetricsForWorkSchedule();
  const visibleProcesses = useVisibleProcesses();
  const { dataDumpToUse, rollupMetric, loading } = useAskrOrMidgardData(
    dataDumpToCheckLoading
  );

  const dataDumpsFullRangeOverrideMachemetryIds = useSelector(
    s => s.legacy.dataDumpsFullRangeOverrideMachemetryIds
  );
  const selectedShift = useSelectedShift();
  const { isGroupTilesPage, isProcessTilesPage } = useGetPageFromPath();
  const { isFullDaySelected } = useDashboardPageContext();
  const {
    isPredictedFutureMetric,
    estimatedFutureMetricPercent,
    isTilesPageFullDay,
    preferredMetric,
  } = useSettings();
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const { translate } = useFlightTranslation();

  const shouldShowPredictedMetric = useMemo(
    () => isPredictedFutureMetric && selectedShift?.range?.lt > Date.now(),
    [isPredictedFutureMetric, selectedShift]
  );
  const groupMetrics: GroupMetricType[] = useMemo(() => {
    const isOver24Hours =
      isGroupTilesPage || isProcessTilesPage
        ? isTilesPageFullDay
        : isFullDaySelected;

    const validMetrics = flightConfigMetrics.filter(m => m.dataDumpKey);

    return (
      (validMetrics.length === 0 ? DEFAULT_ASKR_METRICS : validMetrics)
        .map(m => {
          const customMetric = getCustomMetric(m);

          // Handle custom expression metrics (dataDumpKey starts with "custom:")
          if (
            m.dataDumpKey &&
            typeof m.dataDumpKey === "string" &&
            m.dataDumpKey.startsWith("custom:")
          ) {
            const expression = m.dataDumpKey.substring("custom:".length);
            const value = calculateCustomMetric(expression, dataDumpToUse);
            const metric = {
              ...m,
              value,
              displayName: translate(m.displayName),
            };
            return metric;
          }

          if (!customMetric) {
            // handle overrideMachemetryIds
            if (m.overrideMachemetryIds) {
              const customDataDump =
                dataDumpsFullRangeOverrideMachemetryIds[
                  m.overrideMachemetryIds.join(",")
                ];
              const value = m.dataDumpKey
                ? (customDataDump?.[m.dataDumpKey] as number)
                : null;
              return {
                ...m,
                value,
                displayName: translate(m.displayName),
              };
            }
            // map to dataDump value
            return {
              ...m,
              value: m.dataDumpKey
                ? (dataDumpToUse?.[m.dataDumpKey] as number)
                : null,
              displayName: translate(m.displayName),
            };
          } else {
            // handle CUSTOM_METRICS like current_performance_percent
            const { description, getValue } = customMetric;
            const value = getValue(dataDumpToUse);
            return {
              ...m,
              value,
              displayName: translate(m.displayName),
              description: description && translate(description),
            };
          }
        })
        // calc metric or predicted future metric
        .map(m => {
          const customMetric = getCustomMetric(m);

          // Skip further processing for custom expression metrics
          if (
            m.dataDumpKey &&
            typeof m.dataDumpKey === "string" &&
            m.dataDumpKey.startsWith("custom:")
          ) {
            return m;
          }

          if (
            customMetric ||
            (m.overrideMachemetryIds && m.overrideMachemetryIds?.length > 0)
          ) {
            return m;
          }
          return {
            ...m,
            value: shouldShowPredictedMetric
              ? (getPredictedFutureMetric({
                  isOver24Hours,
                  dataDumpKey: m.dataDumpKey as keyof DataDumpAggregateType,
                  selectedShift,
                  rollupDisplay: rollupMetric,
                  numProcesses: visibleProcesses.length,
                  estimatedFutureMetricPercent,
                  dataDumpsObjForFullRange: dataDumpToUse,
                  isMidgardDDAEnabled,
                  getNumbers: true,
                }) as number | undefined)
              : // use calcMetric here instead of calcPreferredMetric

                // because we want to calculate each metric, not just the preferred one
                calcMetric({
                  rollupMetric,
                  dataDumpKey: m.dataDumpKey as keyof DataDumpAggregateType,
                  dataDumpsObjForRange: dataDumpToUse,
                  isMidgardDDAEnabled,
                }),
          };
        })
    );
  }, [
    dataDumpToUse,
    dataDumpsFullRangeOverrideMachemetryIds,
    isMidgardDDAEnabled,
    rollupMetric,
    selectedShift,
    shouldShowPredictedMetric,
    translate,
    visibleProcesses,
    estimatedFutureMetricPercent,
    flightConfigMetrics,
    isGroupTilesPage,
    isProcessTilesPage,
    isFullDaySelected,
    isTilesPageFullDay,
  ]);

  const invalidMetrics = useMemo(
    () => groupMetrics.filter(m => !m.dataDumpKey),
    [groupMetrics]
  );
  if (invalidMetrics.length) {
    console.log("ignoring invalid metrics:", invalidMetrics);
  }

  const preferredMetricData = useMemo(
    () =>
      groupMetrics.find(m => m.dataDumpKey === preferredMetric) ??
      ({
        displayName: "",
        units: "",
      } as GroupMetricType),
    [groupMetrics, preferredMetric]
  );
  const [{ metricsRawByWorkSchedule }] = useFlightConfig();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const metricsRaw = metricsRawByWorkSchedule[workScheduleId];
  const filterByFlightConfig = useFilterByFlightConfig();
  const groupMetricsFilteredByPage = useMemo(
    () => groupMetrics.filter(m => filterByFlightConfig(m)),
    [groupMetrics, filterByFlightConfig]
  );
  return {
    metrics: groupMetricsFilteredByPage,
    metricsRaw,
    preferredMetricData,
    shouldShowPredictedMetric,
    loading,
  };
}

function getCustomMetric(m: GroupMetricType) {
  return (
    m.dataDumpKey &&
    Object.entries(CUSTOM_METRICS).find(([key]) => key === m.dataDumpKey)?.[1]
  );
}

/** enable Midgard DDA for customers who use non-standard metrics like OEE
 *
 * if customer uses Midgard DDA, they can access non-standard metrics,
 * but we need to fetch aggregate data from Midgard instead of Askr
 */
export function useIsMidgardDDAEnabled() {
  const metrics = useMetricsForWorkSchedule();
  const { flightOverrideIsMidgardEnabled } = useFlagsWithDefaults();
  const { isTrendsPage } = useGetPageFromPath();
  // customer needs Midgard DDA if metrics include non-standard metrics
  return useMemo(
    () =>
      flightOverrideIsMidgardEnabled === "enabled"
        ? true
        : flightOverrideIsMidgardEnabled === "disabled"
          ? false
          : !isTrendsPage &&
            metrics.some(
              metric => !DEFAULT_ASKR_METRIC_KEYS.includes(metric.dataDumpKey)
            ),
    [flightOverrideIsMidgardEnabled, metrics, isTrendsPage]
  );
}
const DEFAULT_ASKR_METRIC_KEYS = DEFAULT_ASKR_METRICS.map(
  metric => metric.dataDumpKey
);

/** combined rollupMetric or dataDump displayed for all visible processes */
function useAskrOrMidgardData(dataDumpToCheckLoading?: DataDumpAggregateType) {
  const dataDumpsObjForFullRange = useFullRangeDataDump();
  const processIdOnSingleProcessPage = useProcessIdOnSingleProcessPage();
  const singleProcessDataDump = useSingleProcessFullRangeDataDump(
    processIdOnSingleProcessPage
  );
  const isSingleProcessPage = useIsSingleProcessPage();

  const allRollups = useFilteredRollups();
  const shouldUseDataDumps = useShouldUseDataDumpsForGroupMetrics();
  const mapDataDumpToRollup = useMapDataDumpToRollup();

  return useMemo(() => {
    const dataDump =
      dataDumpToCheckLoading ??
      (isSingleProcessPage ? singleProcessDataDump : dataDumpsObjForFullRange);
    const rollupMetric =
      shouldUseDataDumps && dataDump
        ? mapDataDumpToRollup(dataDump)
        : addRollupValues(allRollups);

    return {
      dataDumpToUse: dataDump,
      rollupMetric,
      loading: !dataDump && rollupMetric.range.ge === 0,
    };
  }, [
    allRollups,
    mapDataDumpToRollup,
    shouldUseDataDumps,
    dataDumpToCheckLoading,
    isSingleProcessPage,
    singleProcessDataDump,
    dataDumpsObjForFullRange,
  ]);
}
